import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { UserRole } from './types/UserRole';
import ChangePasswordRequired from './pages/ChangePasswordRequired';
import ProtectedRoute from './components/ProtectedRoute';
import LayoutBase from './components/layout/LayoutBase';

// Páginas Admin
import Login from './pages/Login';
import AdminDashboard from './pages/Admin/Dashboard';
import CadastroVeiculos from './pages/Admin/CadastroVeiculos';
import SolicitacaoViagem from './pages/Admin/SolicitacaoViagem';
import Abastecimento from './pages/Admin/Abastecimento';
import Relatorios from './pages/Admin/Relatorios';
import Funcionarios from './pages/Admin/Funcionarios';
import CheckIn from './pages/Admin/CheckIn';
import Provedor from './pages/Admin/Provedor';
import Departamento from './pages/Admin/Departamento';
import IncidentesPage from './pages/Admin/Incidentes';
import ManutencaoPage from './pages/Admin/Manutenção';
import RastreioPage from './pages/Admin/Rastreio';
import HistoricoDeViagens from './pages/Admin/HistoricoDeViagens';
import HistoricoDeManutencao from './pages/Admin/HistoricoDeManutenção';
import HistoricoDeIncidentes from './pages/Admin/HistoricoDeIncidentes';
import HistoricoDeAbastecimento from './pages/Admin/HistoricoDeAbastecimento';
import HistoricoDeCheckIn from './pages/Admin/HistoricoDeCheck-In';

// Páginas Client
import ClientDashboard from './pages/Client/Dashboard';
import ClientSolicitacaoViagem from './pages/Client/SolicitacaoViagem';
import ClientFuncionarios from './pages/Client/Funcionarios';
import ClientCadastroVeiculos from './pages/Client/CadastroVeiculos';
import ClientAbastecimento from './pages/Client/Abastecimento';
import ClientCheckIn from './pages/Client/CheckIn';
import ClientProvedor from './pages/Client/Provedor';
import ClientDepartamento from './pages/Client/Departamento';
import ClientIncidentes from './pages/Client/Incidentes';
import ClientManutencao from './pages/Client/Manutenção';
import ClientRastreio from './pages/Client/Rastreio';
import ClientHistoricoDeViagens from './pages/Client/HistoricoDeViagens';
import ClientHistoricoDeIncidentes from './pages/Client/HistoricoDeIncidentes';
import ClientHistoricoDeAbastecimento from './pages/Client/HistoricoDeAbastecimento';
import ClientHistoricoDeCheckIn from './pages/Client/HistoricoDeCheck-In';
import ClientRelatorios from './pages/Client/Relatorios';
import ClientDepartmentRouteGuard from './pages/Client/components/ClientDepartmentRouteGuard';

// Páginas User
import UserSolicitacaoViagem from './pages/User/SolicitacaoViagem';
import UserCadastroVeiculos from './pages/User/CadastroVeiculos';
import UserHistoricoViagens from './pages/User/HistoricoDeViagens';
import UserAbastecimento from './pages/User/Abastecimento';
import UserRegistroIncidentes from './pages/User/Incidentes';
import UserRelatorioPosUso from './pages/User/RelatorioPosUso';
import UserCheckIn from './pages/User/CheckIn';
import UserManutencao from './pages/User/Manutenção';
import Profile from './pages/Profile';

const LogoutAndRedirect = () => {
  const { logout } = useAuth();
  useEffect(() => {
    void logout();
  }, [logout]);
  return <Navigate to="/" replace />;
};

const AppRoutes = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3 text-gray-600">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          <span>Carregando...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (user.must_change_password) {
    return (
      <Routes>
        <Route path="/change-password" element={<ChangePasswordRequired />} />
        <Route path="/logout" element={<LogoutAndRedirect />} />
        <Route path="*" element={<Navigate to="/change-password" replace />} />
      </Routes>
    );
  }

  if (user.role === UserRole.ADMIN) {
    return (
      <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
        <LayoutBase role={UserRole.ADMIN}>
          <Routes>
            <Route path="/profile" element={<Profile />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/veiculos" element={<CadastroVeiculos />} />
            <Route path="/admin/funcionarios" element={<Funcionarios />} />
            <Route path="/admin/provedores" element={<Provedor />} />
            <Route path="/admin/departamento" element={<Departamento />} />
            <Route path="/admin/abastecimento" element={<Abastecimento />} />
            <Route path="/admin/check-in" element={<CheckIn />} />
            <Route path="/admin/solicitacao-viagem" element={<SolicitacaoViagem />} />
            <Route path="/admin/relatorios" element={<Relatorios />} />
            <Route path="/admin/incidentes" element={<IncidentesPage />} />
            <Route path="/admin/manutencao" element={<ManutencaoPage />} />
            <Route path="/admin/rastreio" element={<RastreioPage />} />
            <Route path="/admin/historico-viagens" element={<HistoricoDeViagens />} />
            <Route path="/admin/historico-manutencao" element={<HistoricoDeManutencao />} />
            <Route path="/admin/historico-incidentes" element={<HistoricoDeIncidentes />} />
            <Route path="/admin/historico-abastecimento" element={<HistoricoDeAbastecimento />} />
            <Route path="/admin/historico-check-in" element={<HistoricoDeCheckIn />} />
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
        </LayoutBase>
      </ProtectedRoute>
    );
  }
  if (user.role === UserRole.CLIENT) {
    return (
      <ProtectedRoute allowedRoles={[UserRole.CLIENT]}>
        <ClientDepartmentRouteGuard>
          <LayoutBase role={UserRole.CLIENT}>
            <Routes>
              <Route path="/profile" element={<Profile />} />
              <Route path="/client/dashboard" element={<ClientDashboard />} />
              <Route path="/client/veiculos" element={<ClientCadastroVeiculos />} />
              <Route path="/client/funcionarios" element={<ClientFuncionarios />} />
              <Route path="/client/abastecimento" element={<ClientAbastecimento />} />
              <Route path="/client/check-in" element={<ClientCheckIn />} />
              <Route path="/client/solicitacao-viagem" element={<ClientSolicitacaoViagem />} />
              <Route path="/client/provedores" element={<ClientProvedor />} />
              <Route path="/client/departamento" element={<ClientDepartamento />} />
              <Route path="/client/fornecedores" element={<ClientProvedor />} />
              <Route path="/client/incidentes" element={<ClientIncidentes />} />
              <Route path="/client/manutencao" element={<ClientManutencao />} />
              <Route path="/client/rastreio" element={<ClientRastreio />} />
              <Route path="/client/historico-viagens" element={<ClientHistoricoDeViagens />} />
              <Route path="/client/historico-incidentes" element={<ClientHistoricoDeIncidentes />} />
              <Route path="/client/historico-abastecimento" element={<ClientHistoricoDeAbastecimento />} />
              <Route path="/client/historico-check-in" element={<ClientHistoricoDeCheckIn />} />
              <Route path="/client/relatorios" element={<ClientRelatorios />} />
              <Route path="*" element={<Navigate to="/client/dashboard" replace />} />
            </Routes>
          </LayoutBase>
        </ClientDepartmentRouteGuard>
      </ProtectedRoute>
    );
  }
  if (user.role === UserRole.USER) {
    return (
      <ProtectedRoute allowedRoles={[UserRole.USER]}>
        <LayoutBase role={UserRole.USER}>
          <Routes>
            <Route path="/profile" element={<Profile />} />
            <Route path="/user/veiculos" element={<UserCadastroVeiculos />} />
            <Route path="/user/solicitacao-viagem" element={<UserSolicitacaoViagem />} />
            <Route path="/user/historico-viagens" element={<UserHistoricoViagens />} />
            <Route path="/user/abastecimento" element={<UserAbastecimento />} />
            <Route path="/user/check-in" element={<UserCheckIn />} />
            <Route path="/user/registro-incidentes" element={<UserRegistroIncidentes />} />
            <Route path="/user/relatorio-pos-uso" element={<UserRelatorioPosUso />} />
            <Route path="/user/manutencao" element={<UserManutencao />} />
            <Route path="*" element={<Navigate to="/user/historico-viagens" replace />} />
          </Routes>
        </LayoutBase>
      </ProtectedRoute>
    );
  }
  // Fallback: show login
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export default AppRoutes;
